import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { Undo2, X } from "lucide-react";

const SEEN_KEY = "glm-swipe-hint-v1";

/* tutoriel d'accueil mobile : « glissez depuis le bord pour revenir »
   affiché une seule fois (localStorage), uniquement sur écran tactile */
export default function SwipeHint() {
    const { lang } = useLang();
    const [show, setShow] = useState(false);

    useEffect(() => {
        const isTouch = window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
        if (!isTouch) return undefined;
        if (localStorage.getItem(SEEN_KEY)) return undefined;
        const t = setTimeout(() => setShow(true), 2200);
        const dismiss = () => {
            localStorage.setItem(SEEN_KEY, "1");
            setShow(false);
        };
        window.addEventListener("touchstart", dismiss, { once: true, passive: true });
        const auto = setTimeout(dismiss, 7500);
        return () => {
            clearTimeout(t);
            clearTimeout(auto);
            window.removeEventListener("touchstart", dismiss);
        };
    }, []);

    const close = () => {
        localStorage.setItem(SEEN_KEY, "1");
        setShow(false);
    };

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    data-testid="swipe-hint"
                    initial={{ opacity: 0, y: 30, x: "-50%" }}
                    animate={{ opacity: 1, y: 0, x: "-50%" }}
                    exit={{ opacity: 0, y: 30, x: "-50%" }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="fixed bottom-24 left-1/2 z-[95] w-[88vw] max-w-[340px] border border-gold/40 bg-ink/95 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl"
                >
                    <button
                        data-testid="swipe-hint-close"
                        onClick={close}
                        aria-label="Fermer"
                        className="absolute right-3 top-3 text-cream/40 transition-colors hover:text-cream"
                    >
                        <X size={14} />
                    </button>
                    {/* démonstration animée du geste */}
                    <div className="relative flex h-14 items-center overflow-hidden border-b border-white/10">
                        {/* bord gauche */}
                        <span className="absolute left-0 top-0 h-full w-[3px] bg-gold/60" />
                        <motion.span
                            className="absolute flex h-10 w-10 items-center justify-center rounded-full border border-gold bg-gold/15 text-gold"
                            initial={{ x: 0 }}
                            animate={{ x: [0, 90, 90, 0] }}
                            transition={{ duration: 2.2, repeat: Infinity, times: [0, 0.45, 0.7, 1], ease: "easeInOut" }}
                        >
                            <Undo2 size={16} />
                        </motion.span>
                        {/* piste en pointillés */}
                        <span className="absolute left-12 right-4 top-1/2 border-t border-dashed border-gold/30" />
                    </div>
                    <p className="mt-3 text-center font-mono text-[9px] leading-relaxed tracking-[0.25em] text-cream/70">
                        {lang === "fr" ? "ASTUCE — GLISSEZ DEPUIS LE BORD GAUCHE POUR REVENIR EN ARRIÈRE" : "TIP — SWIPE FROM THE LEFT EDGE TO GO BACK"}
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
